import { useRef, useCallback } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Type } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const initialized = useRef(false);

  const editorRefCallback = useCallback((node) => {
    if (node && !initialized.current) {
      node.innerHTML = DOMPurify.sanitize(value || "");
      initialized.current = true;
    }
    editorRef.current = node;
  }, [value]);

  const execCmd = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const handleInput = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const ToolBtn = ({ onClick, children, title }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      title={title}
      className="h-8 w-8 p-0 text-[#5C3A2E]/60 hover:text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-lg"
    >
      {children}
    </Button>
  );

  return (
    <div className="border border-[#EADBC8] rounded-xl overflow-hidden bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-[#EADBC8]/50 bg-[#F7F3EF]/50">
        <ToolBtn onClick={() => execCmd("bold")} title="Bold"><Bold className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => execCmd("italic")} title="Italic"><Italic className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => execCmd("underline")} title="Underline"><Underline className="w-3.5 h-3.5" /></ToolBtn>
        <div className="w-px h-5 bg-[#EADBC8]/50 mx-1" />
        <ToolBtn onClick={() => execCmd("formatBlock", "H3")} title="Heading"><Heading2 className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => execCmd("formatBlock", "P")} title="Paragraph"><Type className="w-3.5 h-3.5" /></ToolBtn>
        <div className="w-px h-5 bg-[#EADBC8]/50 mx-1" />
        <ToolBtn onClick={() => execCmd("insertUnorderedList")} title="Bullet List"><List className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => execCmd("insertOrderedList")} title="Numbered List"><ListOrdered className="w-3.5 h-3.5" /></ToolBtn>
      </div>
      <div
        ref={editorRefCallback}
        contentEditable
        onInput={handleInput}
        data-testid="rich-text-editor"
        data-placeholder={placeholder || "Start writing..."}
        className="p-4 min-h-[150px] text-[#2B2B2B] text-sm leading-relaxed outline-none focus:ring-0 [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-[#2B2B2B]/30 prose prose-sm max-w-none [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-[#5C3A2E] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
        suppressContentEditableWarning
      />
    </div>
  );
}
